/**
 * Landing page A/B test router.
 *
 * Randomly assigns first-time visitors to one of four landing page variants,
 * and persists the assignment in a cookie so they see the same variant on return
 * (B120-1: no analytics event; the site loads no analytics).
 *
 * Direct routes (/home-a … /home-d) are also available for QA / previewing.
 */
import React, { useMemo } from "react";
import { SEOHead } from "../../components/seo/SEOHead.tsx";
import { WireframeA } from "./a-filmstrip.tsx";
import { WireframeB } from "./b-wall.tsx";
import { WireframeC } from "./c-editorial.tsx";
import { WireframeD } from "./d-workstation.tsx";

export type VariantKey = "a" | "b" | "c" | "d";

const VARIANTS: Record<VariantKey, React.FC> = {
  a: WireframeA,
  b: WireframeB,
  c: WireframeC,
  d: WireframeD,
};

/** (session 10; owner 2026-10-08) the variants SERVED, `/` and the previews alike. C is off: invented numbers, a fake live
 *  feed and made-up testimonials (COPY-PROPOSALS-2026-10-08 §1). Reversible: add "c" back here. A visitor whose cookie
 *  holds a variant no longer served is assigned again. */
export const SERVED_VARIANTS: readonly VariantKey[] = ["a", "b", "d"];

const COOKIE_NAME = "tf_lp_variant";

function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function setCookie(name: string, value: string, days = 30) {
  if (typeof document === "undefined") return;
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires};path=/;SameSite=Lax`;
}

function pickVariant(): VariantKey {
  // Check for existing assignment
  const saved = getCookie(COOKIE_NAME) as VariantKey | null;
  if (saved && SERVED_VARIANTS.includes(saved)) return saved;

  // an equal split across the variants served
  const keys = SERVED_VARIANTS;
  const pick = keys[Math.floor(Math.random() * keys.length)];

  setCookie(COOKIE_NAME, pick);

  return pick;
}

/**
 * Renders a randomly-assigned landing page variant (A/B/C/D).
 * Use this as the default `/` route.
 */
export function LandingABRouter() {
  const variant = useMemo(pickVariant, []);
  const Component = VARIANTS[variant];
  return (
    <>
      <SEOHead
        title="TubeForge — AI Faceless YouTube Video Generator"
        description="Turn a topic into a fully rendered YouTube video in a few steps. Script, storyboard, voiceover, captions — you approve every gate before anything renders."
        path="/"
      />
      <Component />
    </>
  );
}

/**
 * Force a specific variant — for direct preview routes like `/home-a`.
 */
export function LandingVariantPreview({ variant }: { variant: VariantKey }) {
  if (!SERVED_VARIANTS.includes(variant)) return <LandingABRouter />;   // (session 10) a variant switched off isn't previewed either
  const Component = VARIANTS[variant];
  const labels: Record<VariantKey, string> = {
    a: "Filmstrip Pipeline",
    b: "Wall of Outputs",
    c: "Editorial",
    d: "Workstation",
  };
  return (
    <>
      <SEOHead
        title={`TubeForge — ${labels[variant]} Preview`}
        description="Preview a TubeForge landing page variant."
        path={`/home-${variant}`}
        noindex={true}
      />
      <Component />
    </>
  );
}
